/**
 * In-browser API mock for the modular contract builder prototype: the label,
 * its payees and catalogue (for the builder's pickers), three contracts (to
 * make a sub-contract under), and a contract save
 * that answers like the API so "Save contract" goes through. Every other call
 * keeps dev mode's empty default.
 *
 * Scaffolding — delete with the rest of src/prototypes/modular-contract-builder/.
 */
import { API_BASE_URL } from "@/lib/api/core";
import { jsonResponse, paginated, registerDevMockRoutes } from "@/lib/dev-mode";
import {
  CONTRACTS,
  EXTRACTED_CONTRACT,
  LABEL,
  PAYEES,
  RECORDINGS,
  RELEASES,
  recordingsOn,
  releaseIdsOf,
  releasesOf,
} from "./fixtures";
import {
  linkTracks,
  linkedTracks,
  saveContract,
  savedContract,
  savedContracts,
  savedPayees,
  saveContractExtras,
  savePayee,
  setLinkedTracks,
  unlinkTrack,
} from "./store";

const nowIso = () => new Date().toISOString();

/** How long "processing" an uploaded contract PDF takes. */
const EXTRACTION_MS = 2500;

/** An uploaded contract whose processing time is up: filled in with what was "found". */
function finishExtraction(id: string) {
  const saved = savedContract(id);
  if (saved?.extracting_status !== "processing") return;
  if (Date.now() < Number(saved.extraction_ready_at)) return;
  saveContract({ ...saved, ...EXTRACTED_CONTRACT.fields, id, extracting_status: "finished" });
  setLinkedTracks(id, EXTRACTED_CONTRACT.tracks);
  saveContractExtras(id, EXTRACTED_CONTRACT.extras);
}

function releaseSummary(releaseId: string) {
  const release = RELEASES.find((r) => r.id === releaseId);
  if (!release) return null;
  return {
    id: release.id,
    title: release.title,
    type: release.type,
    release_date: release.release_date,
    catalog_number: release.catalog_number,
    upc_code: release.upc_code,
    artwork: null,
    tracks_count: recordingsOn(release.id).length,
    // The release list's name for the same count.
    track_count: recordingsOn(release.id).length,
    contributors: [
      {
        id: `contrib-${release.id}`,
        display_name:
          ("artist" in release ? release.artist : recordingsOn(release.id)[0]?.artist) ?? "",
        role: "primary_artist",
        country: "",
      },
    ],
  };
}

/** The contracts a track is linked to — the sample ones and any saved — as the API lists them. */
function contractsOf(recordingId: string) {
  const sample = CONTRACTS.map((c) => ({
    id: c.id,
    name: c.name,
    tracks: linkedTracks(c.id) ?? c.tracks,
  }));
  const saved = savedContracts()
    .filter((c) => !CONTRACTS.some((f) => f.id === c.id))
    .map((c) => ({
      id: String(c.id),
      name: String(c.name ?? ""),
      tracks: linkedTracks(String(c.id)) ?? [],
    }));
  return [...sample, ...saved]
    .filter((c) => c.tracks.includes(recordingId))
    .map(({ id, name }) => ({ id, name }));
}

function recordingPayload(recording: (typeof RECORDINGS)[number]) {
  // Each release with the track's number on it — as the API's release tracks give it.
  const releases = releasesOf(recording)
    .map(({ release, track_number }) => {
      const summary = releaseSummary(release);
      return summary && { ...summary, track_number };
    })
    .filter((r) => r !== null);
  return {
    id: recording.id,
    title: recording.title,
    mix_name: recording.mix_name,
    track_number: recording.track_number,
    isrc: recording.isrc,
    contributors: [
      {
        id: `contrib-${recording.id}`,
        display_name: recording.artist,
        role: "primary_artist",
        country: "",
      },
    ],
    releases,
    contracts: contractsOf(recording.id),
    contract_ids: contractsOf(recording.id).map((c) => c.id),
    can_delete: true,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
}

/** A contract as the mock holds it: a sample one, or one saved — its payees with any opening balance. */
type MockContract = Omit<(typeof CONTRACTS)[number], "payees"> & {
  payees: { payee: string; share: number; opening_balance?: string | null }[];
};

/** A contract in the API's list shape. */
function contractPayload(contract: MockContract) {
  return {
    id: contract.id,
    name: contract.name,
    contract_type: contract.contract_type,
    status: contract.status,
    start_date: contract.start_date,
    end_date: contract.end_date,
    territory: "worldwide",
    currency: "USD",
    statement_period: "half_yearly",
    label_share_percent: contract.label_share_percent,
    label_share_overrides: null,
    payment_threshold: "50.00",
    advance_amount: "0.00",
    starting_balance: null,
    label: LABEL.id,
    label_name: LABEL.name,
    party: "",
    payees: contract.payees.map(({ payee, share, opening_balance }) => {
      const p = allPayees().find((x) => x.id === payee)!;
      return {
        matched: true,
        payee: { id: p.id, name: p.name, legal_name: p.legal_name, country: p.country },
        share_percentage: share,
        // As saved (e.g. from the builder's Revenue split), for the details page.
        opening_balance: opening_balance ?? null,
      };
    }),
    tracks_count: contract.tracks.length,
    all_tracks_have_isrc: true,
    first_sound_recording_title: RECORDINGS.find((r) => r.id === contract.tracks[0])?.title ?? null,
    // A draft can still be changed; it can be activated once it has tracks and
    // payees with splits — the app's own "complete" rule (isContractComplete).
    can_delete: contract.status === "draft",
    can_activate:
      contract.status === "draft" &&
      contract.tracks.length > 0 &&
      contract.payees.length > 0 &&
      contract.payees.every((p) => p.share > 0),
    created_at: nowIso(),
    updated_at: nowIso(),
  };
}

/** A sample contract as the builder would have saved it, to edit. */
function fixtureAsSaved(
  fixture: (typeof CONTRACTS)[number],
): Record<string, unknown> & { id: string } {
  return {
    id: fixture.id,
    name: fixture.name,
    contract_type: fixture.contract_type,
    status: fixture.status,
    start_date: fixture.start_date,
    end_date: fixture.end_date,
    territory: "worldwide",
    currency: "USD",
    statement_period: "half_yearly",
    payment_threshold: "50.00",
    label_share_percent: fixture.label_share_percent,
    payees: fixture.payees.map((p) => ({ payee: p.payee, share_percentage: p.share })),
  };
}

/**
 * Contract PDFs uploaded in this tab, by contract: kept in memory, as a file
 * can't go in session storage — the saved contract keeps its name. After a
 * reload, the name stays and opening it shows a sample PDF.
 */
const contractFiles = new Map<string, string>();

/** The contract's file, as the API gives it: a URL ending in the file's name. */
function fileUrl(id: string, name: unknown): string | null {
  if (typeof name !== "string" || !name) return null;
  return `${contractFiles.get(id) ?? "/sample-contract.pdf"}#/${encodeURIComponent(name)}`;
}

/** A file sent for a contract: kept, its name to save — or, sent empty, taken off. */
function keepFile(id: string, file: FormDataEntryValue | null): { file_name?: string | null } {
  if (file instanceof File) {
    contractFiles.set(id, URL.createObjectURL(file));
    return { file_name: file.name };
  }
  if (file === "") {
    contractFiles.delete(id);
    return { file_name: null };
  }
  return {};
}

/** A contract saved from the builder, in the API's shape: its own fields over the defaults. */
function savedContractPayload(saved: Record<string, unknown>) {
  const payees =
    (saved.payees as
      | { payee: string; share_percentage: number; opening_balance?: string | null }[]
      | undefined) ?? [];
  const id = String(saved.id);
  const base = contractPayload({
    id,
    name: String(saved.name ?? "New contract"),
    contract_type: String(saved.contract_type ?? "recording"),
    status: String(saved.status ?? "draft"),
    start_date: (saved.start_date as string | undefined) ?? "",
    end_date: (saved.end_date as string | undefined) ?? null,
    label_share_percent: String(saved.label_share_percent ?? "0"),
    payees: payees
      .filter((p) => allPayees().some((x) => x.id === p.payee))
      .map((p) => ({
        payee: p.payee,
        share: Number(p.share_percentage),
        opening_balance: p.opening_balance ?? null,
      })),
    tracks: linkedTracks(id) ?? [],
  });
  return {
    ...base,
    start_date: (saved.start_date as string | undefined) ?? null,
    territory: saved.territory ?? base.territory,
    currency: saved.currency ?? base.currency,
    statement_period: saved.statement_period ?? base.statement_period,
    payment_threshold: saved.payment_threshold ?? base.payment_threshold,
    advance_amount: saved.advance_amount || base.advance_amount,
    created_at: saved.created_at ?? base.created_at,
    extracting_status: saved.extracting_status ?? null,
    file: fileUrl(id, saved.file_name),
  };
}

/** The label's payees: the sample ones, and any created in the prototype. */
function allPayees() {
  return [...PAYEES, ...(savedPayees() as typeof PAYEES)];
}

function matches(values: (string | null | undefined)[], query: string | null): boolean {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  return values.some((v) => (v ?? "").toLowerCase().includes(needle));
}

function basePath(): string {
  return new URL(API_BASE_URL, window.location.origin).pathname.replace(/\/$/, "");
}

function parseBody(body: BodyInit | null | undefined): Record<string, unknown> {
  if (typeof body !== "string") return {};
  try {
    return JSON.parse(body);
  } catch {
    return {};
  }
}

registerDevMockRoutes((request, url) => {
  const path = url.pathname.slice(basePath().length).replace(/\/$/, "") || "/";
  const method = request.method;
  const q = url.searchParams.get("q");

  if (path === "/accounts/labels" && method === "GET") return jsonResponse(paginated([LABEL]));
  if (path.startsWith("/accounts/labels/") && method === "GET") return jsonResponse(LABEL);

  if (path === "/contracts/payees" && method === "GET") {
    return jsonResponse(paginated(allPayees().filter((p) => matches([p.name, p.legal_name], q))));
  }
  // Creating a payee (the Add payee drawer): kept, so it can be found and added.
  if (path === "/contracts/payees" && method === "POST") {
    const body = parseBody(request.body);
    const payee = {
      legal_name: "",
      country: "",
      preferred_currency: "USD",
      ...body,
      id: `payee-${Date.now()}`,
      name: String(body.name ?? "New payee"),
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    savePayee(payee);
    return jsonResponse(payee, 201);
  }
  if (path.startsWith("/contracts/payees/") && method === "GET") {
    const payee = allPayees().find((p) => p.id === path.slice("/contracts/payees/".length));
    return payee ? jsonResponse(payee) : jsonResponse({ detail: "Not found" }, 404);
  }

  if (path === "/catalogue/recordings" && method === "GET") {
    const releaseIds = url.searchParams.get("release_ids")?.split(",");
    const results = RECORDINGS.filter(
      (r) =>
        matches([r.title, r.isrc, r.artist], q) &&
        (!releaseIds || releaseIdsOf(r).some((id) => releaseIds.includes(id))),
    ).map(recordingPayload);
    return jsonResponse(paginated(results));
  }
  if (path === "/catalogue/releases" && method === "GET") {
    const results = RELEASES.filter((r) =>
      matches(
        [
          r.title,
          r.catalog_number,
          r.upc_code,
          "artist" in r ? r.artist : recordingsOn(r.id)[0]?.artist,
        ],
        q,
      ),
    ).map((r) => releaseSummary(r.id));
    return jsonResponse(paginated(results));
  }

  if (path === "/contracts" && method === "GET") {
    // An edited sample contract is listed once, as edited.
    const saved = savedContracts();
    return jsonResponse(
      paginated(
        [
          ...saved.map(savedContractPayload),
          ...CONTRACTS.filter((c) => !saved.some((s) => s.id === c.id)).map(contractPayload),
        ].filter((c) => matches([c.name], q)),
      ),
    );
  }
  if (path.startsWith("/contracts/") && !path.startsWith("/contracts/payees") && method === "GET") {
    const id = path.slice("/contracts/".length);
    finishExtraction(id);
    const fixture = CONTRACTS.find((c) => c.id === id);
    const saved = savedContract(id);
    if (!fixture && !saved) return jsonResponse({ detail: "Not found" }, 404);
    const trackIds = linkedTracks(id) ?? fixture?.tracks ?? [];
    const related_recordings = RECORDINGS.filter((r) => trackIds.includes(r.id)).map(
      recordingPayload,
    );
    return jsonResponse({
      ...(saved ? savedContractPayload(saved) : contractPayload(fixture!)),
      related_recordings,
      related_releases: [
        ...new Map(related_recordings.flatMap((r) => r.releases).map((r) => [r?.id, r])).values(),
      ],
      has_revenue: false,
      has_expenses: false,
      rates_locked: false,
    });
  }

  // Editing: the changes over the contract as it stands — a sample contract is
  // copied in, with its tracks, the first time it's edited.
  if (path.startsWith("/contracts/") && method === "PATCH") {
    const id = path.slice("/contracts/".length);
    const fixture = CONTRACTS.find((c) => c.id === id);
    const current = savedContract(id) ?? (fixture && fixtureAsSaved(fixture));
    if (!current) return jsonResponse({ detail: "Not found" }, 404);
    if (fixture && !linkedTracks(id)) setLinkedTracks(id, fixture.tracks);
    // A file (with any fields) comes as a form; the rest as JSON.
    const changes =
      request.body instanceof FormData
        ? {
            ...Object.fromEntries(
              [...request.body.entries()].filter(([k, v]) => k !== "file" && typeof v === "string"),
            ),
            ...keepFile(id, request.body.get("file")),
          }
        : parseBody(request.body);
    const contract = { ...current, ...changes, id, updated_at: nowIso() };
    saveContract(contract);
    return jsonResponse(savedContractPayload(contract));
  }
  const trackContract = path.match(/^\/catalogue\/recordings\/([^/]+)\/contracts(?:\/([^/]+))?$/);
  if (trackContract && method === "POST") {
    linkTracks(String(parseBody(request.body).contract), [trackContract[1]]);
    return jsonResponse({}, 201);
  }
  if (trackContract?.[2] && method === "DELETE") {
    unlinkTrack(trackContract[2], trackContract[1]);
    return jsonResponse({});
  }

  // Saving: the contract comes back with an id, as the API would — and is kept,
  // so its details page and the contracts list can show it.
  // Uploading a contract PDF: a draft named after the file, "processing" for a
  // moment, then filled in as if read from it (finishExtraction).
  if (path === "/contracts" && method === "POST" && request.body instanceof FormData) {
    const id = `contract-${Date.now()}`;
    const contract = {
      id,
      ...keepFile(id, request.body.get("file")),
      name: String(request.body.get("name") ?? "Imported contract"),
      status: "draft",
      extracting_status: "processing",
      extraction_ready_at: Date.now() + EXTRACTION_MS,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    saveContract(contract);
    return jsonResponse(savedContractPayload(contract), 201);
  }
  if (path === "/contracts" && method === "POST") {
    const body = parseBody(request.body);
    const contract = {
      ...body,
      id: `contract-${Date.now()}`,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    saveContract(contract);
    return jsonResponse(contract, 201);
  }
  if (path === "/catalogue/recordings/contracts/batch-link" && method === "POST") {
    const body = parseBody(request.body);
    linkTracks(String(body.contract_id), (body.sound_recording_ids as string[]) ?? []);
    return jsonResponse({});
  }

  return null;
});
